import React, { useState, useEffect, Suspense, useRef } from "react";
import { Box, Button, Divider, IconButton, ListItemText, MenuItem, Select, Typography } from "@mui/material";
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { makeStyles } from "@mui/styles";
import { Base64 } from 'js-base64';
import { LoadingButton } from "@mui/lab";
import CircularProgress from '@mui/material/CircularProgress';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { getMonacoLanguage } from "utils/codeEditor";
import { useServices, useSnackbar } from "contexts";
import { runCode } from "services";
import { DisplayTimer } from "./Timer";
import useCodingStore from "./CodingStore";
import { CustomTooltip } from "components/InfoTooltip";
import { QuestionsTypes } from "./constant";

const MonacoEditor = React.lazy(() => import("@monaco-editor/react"));

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%", height: "100%",
    border: '2px solid',
    overflow: 'hidden'
  },
  editorHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    minWidth: '180px',
    marginRight: '20px'
  },
  actions: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(2, 3),
  },
  button: {
    height: '30px',
    color: '#fff',
    textTransform: 'none',
  },
  outputBox: {
    paddingTop: theme.spacing(2),
    display: "flex",
    alignItems: "center",
  },
  header: {
    padding: theme.spacing(1, 2),
    fontSize: "0.875rem",
    fontWeight: 500,
  },
  output: {
    padding: 2,
    height: "200px",
    overflowY: "auto",
  },
  boxBody: {
    padding: theme.spacing(2),
    height: "200px",
    overflow: "auto",
    "&::-webkit-scrollbar": { width: '10px' },
  },
  inputArea: {
    width: "100%",
    border: "none",
    outline: "none",
    ...theme.typography['body01-regular'],
    color: theme.palette.shades['clr-white-900'],
    "&::-webkit-scrollbar": { width: '10px' },
    "&:focus": {
      border: "none",
      outline: "none",
    },
    "&:active": {
      border: "none",
      outline: "none",
    },
    "&:focus-visible": {
      border: "none",
      outline: "none",
    }
  },
  renderValue: {
    maxWidth: "150px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "inline-block",
    verticalAlign: "middle",
  },
  timer: {
    backgroundColor: theme.palette.shades['clr-white-900'],
    borderRadius: '4px',
    padding: theme.spacing(0, 1.5),
  }
}));

const SelectLanguage = ({ setLanguage = () => { }, language, options = [], disabled = false }) => {
  const classes = useStyles();

  return (
    <Select
      id="language-select"
      disabled={disabled}
      value={language ? language._id : ""}
      onChange={(e) => {
        const selectedLang = options.find(
          (lang) => lang._id === e.target.value
        );
        setLanguage(selectedLang);
      }}
      displayEmpty
      renderValue={(selected) => {
        if (!selected || !language) {
          return <em style={{ color: "#aaa" }}>Select Language</em>;
        }
        return (
          <span className={classes.renderValue}>
            {language.name}
          </span>
        );
      }}
      sx={{
        backgroundColor: "#fff",
        height: "30px",
        fontSize: 14,
        width: '100%',
        maxWidth: '170px',
        ".MuiSelect-select": {
          display: "flex",
          alignItems: "center",
        },
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            width: 400,
            maxHeight: 300,
            "& .MuiMenuItem-root": {
              whiteSpace: "normal",
              wordBreak: "break-word",
              paddingY: 1,
            },
          },
        },
      }}
    >
      {options.flatMap((lang, idx) => [
        <MenuItem key={lang._id} value={lang._id}>
          <ListItemText primary={lang.name} />
        </MenuItem>,
        idx < options.length - 1 ? <Divider key={`divider-${lang._id}`} /> : null,
      ])}
    </Select>
  );
};

const CodeOutputBox = ({ output, status, message, themeColor = "#1e1e1e" }) => {
  const classes = useStyles();
  const messageStyle = status === 'success' ? { color: 'green' } : { color: 'red' };

  return (
    <Box>
      <Box
        className={classes.outputBox}
        style={{
          backgroundColor: themeColor === '#1e1e1e' ? '#000' : '#E9EDF2',
          color: themeColor === '#1e1e1e' ? '#fff' : '#000'
        }}
      >
        <Box className={classes.header}>
          <Typography variant='body02-semiBold'>
            Output
          </Typography>
        </Box>
      </Box>
      <Box className={classes.boxBody} style={{ backgroundColor: themeColor }}>
        {message && (
          <Typography variant='h6-regular' style={{ ...messageStyle, display: 'block' }}>
            {message}
          </Typography>
        )}
        <Typography variant="body01-medium" style={{ color: themeColor === '#1e1e1e' ? '#fff' : '#000' }}>
          {output}
        </Typography>
      </Box>
    </Box>
  );
};

const CodeInputBox = ({ value, onChange, disabled = false, themeColor = "#1e1e1e" }) => {
  const classes = useStyles();

  return (
    <Box>
      <Box
        className={classes.outputBox}
        style={{
          backgroundColor: themeColor === '#1e1e1e' ? '#000' : '#E9EDF2',
          color: themeColor === '#1e1e1e' ? '#fff' : '#000'
        }}
      >
        <Box className={classes.header}>
          <Typography variant='body02-semiBold'>
            Input
          </Typography>
        </Box>
      </Box>
      <Box className={classes.boxBody}>
        <textarea
          disabled={disabled}
          value={value}
          onChange={onChange}
          rows={12}
          className={classes.inputArea}
          style={{ backgroundColor: themeColor, color: themeColor === '#1e1e1e' ? '#fff' : '#000' }}
          spellCheck={false}
        />
      </Box>
    </Box>
  );
};

const CodeEditor = ({
  languageOptions = [],
  disableEditing = false,
  onSubmit = () => { },
  code,
  setCode,
  language,
  setLanguage,
  codeInput,
  setCodeInput
}) => {
  const classes = useStyles();
  const service = useServices();
  const snackbar = useSnackbar();

  const attempt = useCodingStore(state => state.attempt);
  const sectionTimeData = useCodingStore(state => state.sectionTimeData);
  const questionType = useCodingStore(state => state.questionType);
  const timeLeft = useCodingStore(state => state.timeLeft);

  const [theme, setTheme] = useState("vs-light");
  const [themeColor, setThemeColor] = useState("#fff");
  const [isEditorLoaded, setIsEditorLoaded] = useState(false);
  const [editorWidth, setEditorWidth] = useState(800);
  const [codeOutput, setCodeOutput] = useState('');
  const [outputStatus, setOutputStatus] = useState('');
  const [outputMessage, setOutputMessage] = useState('');
  const [runningCode, setRunningCode] = useState(false);

  const editorContainerRef = useRef(null);
  const isResizingRef = useRef(false);
  const enableCopyPaste = process.env.REACT_APP_STAGE === "dev" || process.env.REACT_APP_STAGE === "staging";

  useEffect(() => {
    setIsEditorLoaded(true);
  }, []);

  // Handlers for resizing the editor
  const handleMouseDown = (e) => {
    isResizingRef.current = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (isResizingRef.current) {
      setEditorWidth(e.clientX - editorContainerRef.current.offsetLeft);
    }
  };

  const handleMouseUp = () => {
    isResizingRef.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  // Execute code
  const handleRunCode = React.useCallback(async () => {
    setRunningCode(true);
    try {
      const encodedCode = Base64.encode(code);
      const encodedInput = Base64.encode(codeInput);
      const result = await service.callService(runCode, {
        languageId: language._id,
        sourceCode: encodedCode,
        stdin: encodedInput,
        base64Encoded: true,
        codeChallengeAttemptId: attempt._id
      });

      const decodedOutput = Base64.decode(result?.output || "");
      setCodeOutput(decodedOutput);
      setOutputStatus(result.status);
      setOutputMessage(result.message);
      setRunningCode(false);
    } catch (err) {
      snackbar.error("Uh Oh! Unable to run code");
      console.error(err.message);
      setRunningCode(false);
    }
  }, [service, language, code]);

  const editorRef = useRef(null);

  function handleEditorDidMount(editor, monaco) {
    editorRef.current = editor;

    if (!enableCopyPaste) {
      // Disabling copy-paste command
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyV, () => {
      });
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyC, () => {
      });
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyX, () => {
      });
      editor.updateOptions({ contextmenu: false });

      // Intercept Monaco paste
      editor.onDidPaste(() => {
        const model = editor.getModel();
        if (model) {
          editor.executeEdits(null, [
            {
              range: editor.getSelection(),
              text: "",
            },
          ]);
        }
      });

      // Intercept browser-level paste/copy/cut
      const domNode = editor.getDomNode();
      if (domNode) {
        domNode.addEventListener("paste", (e) => e.preventDefault());
        domNode.addEventListener("copy", (e) => e.preventDefault());
        domNode.addEventListener("cut", (e) => e.preventDefault());

        domNode.addEventListener("drop", (e) => { e.preventDefault(); e.stopPropagation(); });
        domNode.addEventListener("dragover", (e) => { e.preventDefault(); e.stopPropagation(); });
        domNode.addEventListener("dragenter", (e) => { e.preventDefault(); e.stopPropagation(); });
      }
    } else {
      // Enable context menu in dev
      editor.updateOptions({ contextmenu: true });
    }
  }

  return (
    <Box
      className={classes.root}
      style={{
        backgroundColor: themeColor,
        borderColor: themeColor === '#1e1e1e' ? '#000' : '#E9EDF2',
      }}
    >
      <Box className={classes.editorHead}>
        <Box ml={2} display="flex" alignItems="center" gap={2}>
          <Box id='theme-toggle'>
            <IconButton
              onClick={() => {
                setTheme(theme === "vs-dark" ? "light" : "vs-dark");
                setThemeColor(theme !== "vs-dark" ? "#1e1e1e" : "#fff");
              }}
              style={{ padding: 0 }}
              id='theme-toggle-container'
            >
              <img
                src={
                  theme === "vs-dark"
                    ? "https://assets.languify.in/images/dark-theme.png"
                    : "https://assets.languify.in/images/light-theme.png"
                }
                alt="themeToggleIcon"
                width={36}
                height={36}
              />
            </IconButton>
          </Box>
          {
            sectionTimeData.showTimer && !disableEditing &&
            <>
              <Divider orientation="vertical" flexItem />
              <CustomTooltip
                placement='bottom'
                component={"Make your final code submission within the given time."}
                tooltipStyle={{
                  backgroundColor: '#262B30', textAlign: 'flex-start',
                  border: '1px solid #8692A3'
                }}
                arrowStyle={{ color: '#8692A3' }}
              >
                <Box className={classes.timer}>
                  <DisplayTimer
                    timeLeft={timeLeft}
                    warningTime={sectionTimeData.warningTime}
                  />
                </Box>
              </CustomTooltip>
            </>
          }
        </Box>

        <Box className={classes.actions}>

          <CustomTooltip
            placement='bottom'
            show={!disableEditing && questionType === QuestionsTypes.MAIN}
            component={
              <>
                You can write your code in any format—either just the solution function or the complete code including the main function. What's important is that your approach is efficient, using appropriate data structures and algorithms to achieve optimal time and space complexity.
                <br />Also, ensure that your input handling aligns with your code, and that the output strictly matches the format specified in the problem statement.
              </>
            }
            tooltipStyle={{
              backgroundColor: '#262B30', textAlign: 'flex-start',
              border: '1px solid #8692A3', maxWidth: '400px'
            }}
            arrowStyle={{ color: '#8692A3' }}
          >
            <InfoOutlinedIcon
              style={{ color: theme === "vs-dark" ? '#9DC2F2' : '#003E8C', fontSize: '28px' }}
            />
          </CustomTooltip>

          <Divider orientation="vertical" flexItem />

          {/* Language Selection Field */}
          <SelectLanguage
            setLanguage={setLanguage}
            language={language}
            options={languageOptions}
            disabled={disableEditing}
          />

          {/* Run Code Button */}
          <Box id='run-code'>
            <LoadingButton
              loading={runningCode}
              onClick={handleRunCode}
              startIcon={<PlayArrowRoundedIcon />}
              className={classes.button}
              style={{ backgroundColor: disableEditing ? "#D0D6DD" : '#003E8C' }}
              id='run-code-container'
              disabled={disableEditing}
              loadingIndicator={
                <CircularProgress sx={{ color: '#FFFFFF' }} size={20} />
              }
            >
              Run
            </LoadingButton>
          </Box>

          {/* Submit Button */}
          <Box id='code-submit'>
            <Button
              onClick={onSubmit}
              className={classes.button}
              style={{ backgroundColor: disableEditing ? "#D0D6DD" : '#009970' }}
              disabled={disableEditing}
              id="code-submit-container"
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Monaco Editor with dynamic loading and resizable width */}
      <Box
        ref={editorContainerRef}
        style={{ width: editorWidth }}
      >
        {isEditorLoaded ? (
          <Suspense
            fallback={<div className="text-white">Loading Editor...</div>}
          >
            <MonacoEditor
              height="calc(100vh - 400px)"
              language={language ? getMonacoLanguage(language.name) : 'javascript'}
              theme={theme}
              value={code}
              onChange={(value) => setCode(value)}
              onMount={handleEditorDidMount}
              options={{
                minimap: { enabled: true },
                fontSize: 14,
                wordWrap: "on",
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 2,
                autoClosingBrackets: "always",
                suggestOnTriggerCharacters: true,
                quickSuggestions: true,
                snippetSuggestions: "inline",
                formatOnType: true,
                formatOnPaste: false,
                readOnly: disableEditing,
                dragAndDrop: false,
                // IntelliSense features
                parameterHints: {
                  enabled: true,
                },
                suggest: {
                  showMethods: true,
                  showFunctions: true,
                  showConstructors: true,
                  showFields: true,
                  showVariables: true,
                  showClasses: true,
                  showStructs: true,
                  showInterfaces: true,
                  showModules: true,
                  showProperties: true,
                  showEvents: true,
                  showOperators: true,
                  showUnits: true,
                  showValues: true,
                  showConstants: true,
                  showEnums: true,
                  showEnumMembers: true,
                  showKeywords: true,
                  showWords: true,
                  showColors: true,
                  showFiles: true,
                  showReferences: true,
                  showFolders: true,
                  showTypeParameters: true,
                  showSnippets: true,
                  showUsers: true,
                  showIssues: true,
                },
                hover: {
                  enabled: true,
                  delay: 300,
                },
                lightbulb: {
                  enabled: true,
                }
              }}
            />
          </Suspense>
        ) : (
          <Box className="text-white">Initializing Editor...</Box>
        )}
        <Box onMouseDown={handleMouseDown}></Box>
      </Box>

      {/* Input and Output Boxes Side by Side */}
      <Box display="flex">
        <Box flex={1} borderRight='2px solid #000'>
          <CodeInputBox
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value)}
            disabled={disableEditing}
            themeColor={themeColor}
          />
        </Box>
        <Box flex={1}>
          <CodeOutputBox
            output={codeOutput}
            status={outputStatus}
            message={outputMessage}
            themeColor={themeColor}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CodeEditor;